import { useCallback, useMemo, useState } from 'react';
import { SubcategoryAllocation } from '../types/zoneMapType';
import { TargetStoreState, BatchPublishState } from './types';
import { computeFixtureAllocations, FixtureSequenceEntry } from '../allocation/computeFixtureAllocations';
import { Zone, PublishZoneInput as GqlPublishZoneInput, InputMaybe } from 'src/graphql/generated/graphql';

export interface PublishZoneResult {
  publishZone: {
    error?: { errorMessage: string } | null;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PublishFn = (variables: { input?: InputMaybe<GqlPublishZoneInput> }) => Promise<PublishZoneResult>;

export interface UseMultiStoreStateReturn {
  stores: TargetStoreState[];
  batchPublishState: BatchPublishState;
  updateStoreData: (storeId: string, data: Partial<TargetStoreState>) => void;
  initializeStores: (storeIds: string[]) => void;
  allReady: boolean;
  needsSequenceCount: number;
  executeBatchPublish: (
    allocations: SubcategoryAllocation[],
    publishFn: PublishFn,
    macroplanogramName: string,
    timestamp: number,
    department?: string,
  ) => Promise<void>;
  retryFailed: (
    allocations: SubcategoryAllocation[],
    publishFn: PublishFn,
    macroplanogramName: string,
    timestamp: number,
    department?: string,
  ) => Promise<void>;
  clearState: () => void;
  removeStore: (storeId: string) => void;
}

const initialBatchState: BatchPublishState = {
  isPublishing: false,
  completedCount: 0,
  failedCount: 0,
  totalCount: 0,
};

const useMultiStoreState = (): UseMultiStoreStateReturn => {
  const [stores, setStores] = useState<TargetStoreState[]>([]);
  const [batchPublishState, setBatchPublishState] = useState<BatchPublishState>(initialBatchState);

  const initializeStores = useCallback((storeIds: string[]) => {
    setStores(
      storeIds.map((storeId) => ({
        storeId,
        floorplanData: null,
        fixtureOrderingMap: null,
        fixtureAssignments: null,
        readinessStatus: 'loading',
        publishStatus: 'pending',
      })),
    );
    setBatchPublishState(initialBatchState);
  }, []);

  const updateStoreData = useCallback((storeId: string, data: Partial<TargetStoreState>) => {
    setStores((prev) => prev.map((s) => (s.storeId === storeId ? { ...s, ...data } : s)));
  }, []);

  const allReady = useMemo(() => stores.length > 0 && stores.every((s) => s.readinessStatus === 'ready'), [stores]);

  const needsSequenceCount = useMemo(
    () => stores.filter((s) => s.readinessStatus === 'needs-sequence').length,
    [stores],
  );

  const buildZoneMap = (
    allocations: SubcategoryAllocation[],
    orderingMap: Map<string, number>,
    department?: string,
  ): Zone[] => {
    const fixtureSequence: FixtureSequenceEntry[] = Array.from(orderingMap.entries()).map(
      ([fixtureId, sequenceOrder]) => ({ fixtureId, sequenceOrder }),
    );
    const assignments = computeFixtureAllocations(allocations, fixtureSequence);

    // Group assignments into a single zone for publishing
    return [
      {
        name: allocations
          .map((a) => a.subcategoryName)
          .sort()
          .join('/'),
        subcategoryIds: allocations.map((a) => a.subcategoryId),
        fixtureIds: assignments.map((a) => a.fixtureId),
        facings: 0,
        department: department || undefined,
        allocations: allocations.map((a) => {
          const assignedFixtures = assignments
            .filter(
              (fa) =>
                fa.subcategoryId === a.subcategoryId ||
                (fa.isBoundary && fa.boundaryNames?.includes(a.subcategoryName)),
            )
            .map((fa) => fa.fixtureId);
          return {
            subcategoryId: a.subcategoryId,
            subcategoryName: a.subcategoryName,
            percentage: a.percentage,
            fixtureIds: assignedFixtures.length > 0 ? assignedFixtures : undefined,
          };
        }),
      },
    ];
  };

  const publishStores = useCallback(
    async (
      storesToPublish: TargetStoreState[],
      allocations: SubcategoryAllocation[],
      publishFn: PublishFn,
      macroplanogramName: string,
      timestamp: number,
      department?: string,
    ) => {
      for (const store of storesToPublish) {
        if (!store.fixtureOrderingMap || store.fixtureOrderingMap.size === 0) continue;

        setStores((prev) =>
          prev.map((s) => (s.storeId === store.storeId ? { ...s, publishStatus: 'publishing' as const } : s)),
        );

        try {
          const zoneMap = buildZoneMap(allocations, store.fixtureOrderingMap, department);
          const result = await publishFn({
            input: {
              macroplanogramName,
              storeId: store.storeId,
              zoneMap,
              timestamp,
              isDraft: false,
            },
          });

          if (result.publishZone.error) {
            setStores((prev) =>
              prev.map((s) =>
                s.storeId === store.storeId
                  ? { ...s, publishStatus: 'failed' as const, publishError: result.publishZone.error?.errorMessage }
                  : s,
              ),
            );
            setBatchPublishState((prev) => ({
              ...prev,
              completedCount: prev.completedCount + 1,
              failedCount: prev.failedCount + 1,
            }));
          } else {
            setStores((prev) =>
              prev.map((s) => (s.storeId === store.storeId ? { ...s, publishStatus: 'published' as const } : s)),
            );
            setBatchPublishState((prev) => ({
              ...prev,
              completedCount: prev.completedCount + 1,
            }));
          }
        } catch (err) {
          setStores((prev) =>
            prev.map((s) =>
              s.storeId === store.storeId
                ? {
                    ...s,
                    publishStatus: 'network-error' as const,
                    publishError: err instanceof Error ? err.message : 'Network error',
                  }
                : s,
            ),
          );
          setBatchPublishState((prev) => ({
            ...prev,
            completedCount: prev.completedCount + 1,
            failedCount: prev.failedCount + 1,
          }));
        }
      }
    },
    [],
  );

  const executeBatchPublish = useCallback(
    async (
      allocations: SubcategoryAllocation[],
      publishFn: PublishFn,
      macroplanogramName: string,
      timestamp: number,
      department?: string,
    ) => {
      const readyStores = stores.filter((s) => s.readinessStatus === 'ready');
      setBatchPublishState({
        isPublishing: true,
        completedCount: 0,
        failedCount: 0,
        totalCount: readyStores.length,
      });

      await publishStores(readyStores, allocations, publishFn, macroplanogramName, timestamp, department);

      setBatchPublishState((prev) => ({ ...prev, isPublishing: false }));
    },
    [stores, publishStores],
  );

  const retryFailed = useCallback(
    async (
      allocations: SubcategoryAllocation[],
      publishFn: PublishFn,
      macroplanogramName: string,
      timestamp: number,
      department?: string,
    ) => {
      const failedStores = stores.filter((s) => s.publishStatus === 'failed' || s.publishStatus === 'network-error');
      setBatchPublishState((prev) => ({
        isPublishing: true,
        completedCount: prev.completedCount - failedStores.length,
        failedCount: 0,
        totalCount: prev.totalCount,
      }));

      await publishStores(failedStores, allocations, publishFn, macroplanogramName, timestamp, department);

      setBatchPublishState((prev) => ({ ...prev, isPublishing: false }));
    },
    [stores, publishStores],
  );

  const clearState = useCallback(() => {
    setStores([]);
    setBatchPublishState(initialBatchState);
  }, []);

  const removeStore = useCallback((storeId: string) => {
    setStores((prev) => prev.filter((s) => s.storeId !== storeId));
  }, []);

  return {
    stores,
    batchPublishState,
    updateStoreData,
    initializeStores,
    allReady,
    needsSequenceCount,
    executeBatchPublish,
    retryFailed,
    clearState,
    removeStore,
  };
};

export default useMultiStoreState;
